/**
 * Protected Route Component
 * SECURITY: Ensures users can only access dashboards they're assigned in allowed_dashboards
 * Prevents URL manipulation attacks (e.g., teacher accessing /senior-staff)
 * 
 * NEW (Migration 053): Checks allowed_dashboards array, not just role field
 */

import { ReactElement, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth, useUserRole } from '@hooks/useAuth'
import { supabase } from '@services/supabase'
import { AccountAccessGate } from './AccountAccessGate'

interface ProtectedRouteProps {
  children: ReactElement
  requiredRole: 
    | 'sysadmin'
    | 'school_admin'
    | 'senior_staff'
    | 'department_head'
    | 'teacher'
    | 'ta'
    | 'senco'
    | 'student'
    | 'parent'
    | 'academy_admin'
    | 'academy_user'
    | 'lea_admin'
    | 'lea_user'
    | 'gov_admin'
    | 'gov_user'
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user } = useAuth()
  return (
    <AccountAccessGate key={`${user?.id || 'pending'}:${requiredRole}`} userId={user?.id}>
      <DashboardRoleRoute requiredRole={requiredRole}>{children}</DashboardRoleRoute>
    </AccountAccessGate>
  )
}

const DashboardRoleRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user } = useAuth()
  const { role, loading: roleLoading } = useUserRole(user?.id)
  const [allowedDashboards, setAllowedDashboards] = useState<string[]>([])
  const [defaultDashboard, setDefaultDashboard] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [forceResolve, setForceResolve] = useState(false)

  useEffect(() => {
    const fetchDashboardAccess = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }

      const cacheKey = `omnis_user_dashboards_${user.id}`
      
      // Check cache first for faster initial load (but don't clear it - we'll update it after fetching)
      const cached = sessionStorage.getItem(cacheKey)
      if (cached) {
        try {
          const cachedData = JSON.parse(cached)
          if (cachedData.allowed && Array.isArray(cachedData.allowed) && cachedData.allowed.length > 0) {
            // Use cached data immediately while we fetch fresh data in background
            setAllowedDashboards(cachedData.allowed)
            setDefaultDashboard(cachedData.defaultDash || 'teacher')
            // Don't set loading to false yet - continue to fetch fresh data
          }
        } catch (e) {
        }
      }

      // Add timeout to prevent infinite loading (increased to 10 seconds)
      // Add timeout to prevent infinite loading - batch state updates with requestAnimationFrame
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          // Try cache again as fallback
          const cachedFallback = sessionStorage.getItem(cacheKey)
          if (cachedFallback) {
            try {
              const cachedData = JSON.parse(cachedFallback)
              if (cachedData.allowed && cachedData.allowed.length > 0) {
                setAllowedDashboards(cachedData.allowed)
                setDefaultDashboard(cachedData.defaultDash || 'teacher')
                setLoading(false)
                return
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
          // Last resort fallback
          const fallbackDashboards = role ? [role] : ['teacher']
          const fallbackDefault = role || 'teacher'
          setAllowedDashboards(fallbackDashboards)
          setDefaultDashboard(fallbackDefault)
          setLoading(false)
        })
      }, 10000)

      try {
        let dashboards: string[] = []
        let defaultDash = role || ''

        // First, try to get from RPC (this is the authoritative source)
        const { data, error } = await supabase
          .rpc('get_effective_dashboard_state')

        if (error) {
          console.error('🔐 ProtectedRoute - Failed to get effective dashboard state via RPC:', error)
        }

        const rpcState = Array.isArray(data) ? data[0] : data

        if (rpcState) {
          const stateDashboards = Array.isArray(rpcState.allowed_dashboards)
            ? rpcState.allowed_dashboards.filter((dash: unknown): dash is string => typeof dash === 'string' && dash.length > 0)
            : []

          if (stateDashboards.length > 0) {
            dashboards = stateDashboards
          }

          if (rpcState.default_dashboard) {
            defaultDash = rpcState.default_dashboard
          } else if (rpcState.active_dashboard) {
            defaultDash = rpcState.active_dashboard
          }
          
          // If RPC returned data but dashboards array is empty, use default_dashboard
          if (dashboards.length === 0 && defaultDash) {
            dashboards = [defaultDash]
          }
        }

        // Fallback to direct select if RPC didn't provide data
        if (dashboards.length === 0) {
          const { data: directData, error: directError } = await supabase
            .from('users')
            .select('allowed_dashboards, default_dashboard, email, role')
            .eq('id', user.id)
            .single()

          if (directError) {
            console.error('🔐 ProtectedRoute - Failed to fetch user data directly:', directError)
            throw directError
          }

          const directDashboards = Array.isArray(directData?.allowed_dashboards)
            ? directData.allowed_dashboards.filter((dash: unknown): dash is string => typeof dash === 'string' && dash.length > 0)
            : []

          dashboards = directDashboards
          defaultDash = directData?.default_dashboard || defaultDash || directData?.role || role || ''
        }

        if (dashboards.length === 0) {
          dashboards = defaultDash ? [defaultDash] : role ? [role] : []
        }

        // Cache for this session
        sessionStorage.setItem(cacheKey, JSON.stringify({
          allowed: dashboards,
          defaultDash
        }))

        clearTimeout(timeoutId)
        setAllowedDashboards(dashboards)
        setDefaultDashboard(defaultDash)
      } catch (error) {
        console.error('Failed to fetch dashboard access:', error)
        // Fallback to role - ensure we always have something
        const fallbackDashboards = role ? [role] : ['teacher'] // Default to teacher if no role
        const fallbackDefault = role || 'teacher'
        setAllowedDashboards(fallbackDashboards)
        setDefaultDashboard(fallbackDefault)
      } finally {
        clearTimeout(timeoutId)
        setLoading(false)
      }
    }

    fetchDashboardAccess()
  }, [user?.id, role])

  // Show loading while checking permissions
  // CRITICAL: Must wait for BOTH loading states AND ensure allowedDashboards is fetched
  // ALSO: If we don't have a userId yet, we're still loading auth state
  // FIX: Don't wait forever - if we have a role, use it as fallback
  // EMERGENCY FIX: If loading takes more than 2 seconds, use role fallback immediately
  useEffect(() => {
    // Use requestAnimationFrame to batch state updates
    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        if (loading && !forceResolve) {
          setForceResolve(true)
          setLoading(false)
          const fallbackRole = role || 'teacher'
          setAllowedDashboards([fallbackRole])
          setDefaultDashboard(fallbackRole)
        }
      })
    }, 2000)
    
    return () => clearTimeout(timeout)
  }, [loading, role, forceResolve])
  
  const shouldShowLoading = (loading || roleLoading || !user?.id) && !forceResolve
  
  if (shouldShowLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying permissions...</p>
        </div>
      </div>
    )
  }

  // If we still don't have dashboards but we have a role, use the role as fallback
  if ((allowedDashboards.length === 0 && !defaultDashboard && role) || forceResolve) {
    const effectiveRole = role || 'teacher'
    const hasAccess = effectiveRole === requiredRole
    if (!hasAccess) {
      const dashboardRoutes: Record<string, string> = {
        sysadmin: '/sysadmin',
        school_admin: '/school-admin',
        senior_staff: '/senior-staff',
        department_head: '/department-head',
        teacher: '/teacher',
        ta: '/ta',
        senco: '/senco',
        student: '/student',
        parent: '/parent',
        academy_admin: '/academy-admin',
        academy_user: '/academy-user',
        lea_admin: '/lea-admin',
        lea_user: '/lea-user',
        gov_admin: '/gov-admin',
        gov_user: '/gov-user'
      }
      const redirectTo = dashboardRoutes[effectiveRole] || '/teacher'
      return <Navigate to={redirectTo} replace />
    }
    // Allow access if role matches
    return children
  }

  // SECURITY CHECK: Verify user has access to this dashboard
  // Only check after we've finished loading (allowedDashboards is populated)
  // EXCEPTION: Sysadmins can access any dashboard (they're super users)
  const isSysadmin = role === 'sysadmin' || allowedDashboards.includes('sysadmin')
  const hasAccess = isSysadmin || (allowedDashboards.length > 0 && allowedDashboards.includes(requiredRole))

  if (!hasAccess && allowedDashboards.length > 0) {
    // Only log security warning if we've actually loaded the dashboards and user doesn't have access
    
    // Redirect to their default dashboard
    const dashboardRoutes: Record<string, string> = {
      sysadmin: '/sysadmin',
      school_admin: '/school-admin',
      senior_staff: '/senior-staff',
      department_head: '/department-head',
      teacher: '/teacher',
      ta: '/ta',
      senco: '/senco',
      student: '/student',
      parent: '/parent',
      academy_admin: '/academy-admin',
      academy_user: '/academy-user',
      lea_admin: '/lea-admin',
      lea_user: '/lea-user',
      gov_admin: '/gov-admin',
      gov_user: '/gov-user'
    }

    const redirectTo = dashboardRoutes[defaultDashboard] || '/teacher'
    return <Navigate to={redirectTo} replace />
  }

  // User has access - allow
  // If sysadmin, log that they're accessing as school admin for testing
  if (isSysadmin && requiredRole === 'school_admin') {
  }
  return children
}
